/**
 * productivity.ts — simple, rule-based "productive / neutral / unproductive"
 * classifier for the desktop-agent activity data (apps, browser sites, files).
 *
 * WHY THIS EXISTS
 *   The backend captures *what* ran (app_name, url, window title, file_path,
 *   duration) but never says whether it was useful work. This maps each of
 *   those raw signals to one of three buckets so the dashboard can show it.
 *
 * HOW TO TUNE IT  ← this is the part you edit
 *   Everything is driven by the keyword lists below (WEB, APP, FILE_EXT).
 *   Matching is a case-insensitive substring test, checked in priority order:
 *   productive → neutral → unproductive. Add/move a keyword to reclassify.
 *
 *   Categories are role-dependent: YouTube is a distraction for an accountant
 *   but core work for a video editor; WhatsApp/Discord may be work comms on
 *   some teams. Adjust the lists for how *your* team actually works. Later this
 *   can be lifted into an admin-configurable, per-department setting.
 */

export type Category = "productive" | "neutral" | "unproductive";

/** Display metadata for each bucket (light hex, matches the dashboard style). */
export const CATEGORY_META: Record<
  Category,
  { label: string; fg: string; bg: string; dot: string }
> = {
  productive: {
    label: "Productive",
    fg: "#15803d",
    bg: "#dcfce7",
    dot: "#22c55e",
  },
  neutral: { label: "Neutral", fg: "#6b7280", bg: "#f3f4f6", dot: "#9ca3af" },
  unproductive: {
    label: "Unproductive",
    fg: "#b91c1c",
    bg: "#fee2e2",
    dot: "#ef4444",
  },
};

/* ─── Web rules (browser sites) ─────────────────────────────
   Matched against  host + " " + title + " " + url  (all lowercase).
   Order within a bucket does not matter; buckets are checked
   productive → neutral → unproductive, so a keyword in an earlier
   bucket wins. Keep specific tokens (docs.google) ahead of generic
   ones (google.com) by putting them in the higher-priority bucket. */
const WEB: Record<Category, string[]> = {
  productive: [
    "github",
    "gitlab",
    "bitbucket",
    "stackoverflow",
    "stack overflow",
    "developer.mozilla",
    "mdn web",
    "chatgpt",
    "openai",
    "claude.ai",
    "gemini.google",
    "bard.google",
    "npmjs",
    "pypi.org",
    "jira",
    "atlassian",
    "confluence",
    "notion.so",
    "linear.app",
    "figma",
    "vercel",
    "netlify",
    "aws.amazon",
    "console.cloud.google",
    "portal.azure",
    "postgresql",
    "w3schools",
    "geeksforgeeks",
    "leetcode",
    "docs.google",
    "sheets.google",
    "teamlio",
    "startupkhata",
    "localhost",
    "127.0.0.1",
  ],
  neutral: [
    "mail.google",
    "gmail",
    "outlook",
    "calendar.google",
    "drive.google",
    "google.com",
    "bing.com",
    "duckduckgo",
    "translate.google",
  ],
  unproductive: [
    "youtube",
    "youtu.be",
    "netflix",
    "primevideo",
    "hotstar",
    "disneyplus",
    "hulu",
    "twitch",
    "instagram",
    "facebook",
    "fb.com",
    "twitter",
    "x.com",
    "reddit",
    "tiktok",
    "snapchat",
    "pinterest",
    "9gag",
    "spotify",
    "soundcloud",
  ],
};

/* ─── Application rules (native apps) ───────────────────────
   Matched against app_name / app_exe (lowercase). Browsers are NOT
   here — a browser's productivity comes from the site it's showing
   (see WEB + isBrowserApp). */
const APP: Record<Category, string[]> = {
  productive: [
    "code",
    "devenv",
    "visual studio",
    "intellij",
    "pycharm",
    "webstorm",
    "goland",
    "datagrip",
    "clion",
    "rider",
    "sublime",
    "notepad++",
    "windowsterminal",
    "wt.exe",
    "powershell",
    "pwsh",
    "cmd.exe",
    "bash",
    "git",
    "docker",
    "postman",
    "insomnia",
    "dbeaver",
    "pgadmin",
    "mysqlworkbench",
    "excel",
    "winword",
    "powerpnt",
    "onenote",
    "acrobat",
    "figma",
    "photoshop",
    "illustrator",
    "premiere",
    "aftereffects",
    "blender",
    "unity",
    "obsidian",
    "slack",
    "teams",
    "ms-teams",
    "zoom",
    "claude",
    "chatgpt",
    "workflow",
    "teamlio",
    "notion",
  ],
  neutral: [
    "explorer",
    "outlook",
    "searchapp",
    "searchhost",
    "shellexperiencehost",
    "startmenuexperiencehost",
    "systemsettings",
    "taskmgr",
    "lockapp",
    "textinputhost",
    "applicationframehost",
    "snippingtool",
    "screenclippinghost",
    "calculator",
    "notepad",
    "photos",
  ],
  unproductive: [
    "spotify",
    "steam",
    "epicgames",
    "riotclient",
    "leagueclient",
    "vlc",
    "netflix",
    "discord",
    "whatsapp",
    "telegram",
    "minecraft",
  ],
};

/** Browser process names — classified by the site they show, not the app. */
const BROWSERS = [
  "chrome",
  "brave",
  "msedge",
  "edge",
  "firefox",
  "opera",
  "opera_gx",
  "chromium",
  "safari",
  "iexplore",
  "vivaldi",
];

/** File extensions → category (files opened in Explorer / editors). */
const FILE_EXT: Record<Category, string[]> = {
  productive: [
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "csv",
    "txt",
    "md",
    "json",
    "xml",
    "yml",
    "yaml",
    "env",
    "py",
    "js",
    "ts",
    "tsx",
    "jsx",
    "java",
    "go",
    "rs",
    "c",
    "cpp",
    "h",
    "hpp",
    "cs",
    "html",
    "css",
    "sql",
    "sh",
    "ipynb",
    "psd",
    "ai",
    "fig",
  ],
  neutral: ["zip", "rar", "7z", "log", "tmp", "ini", "cfg"],
  unproductive: [
    "mp4",
    "mkv",
    "avi",
    "mov",
    "webm",
    "mp3",
    "wav",
    "flac",
    "m4a",
  ],
};

export function isBrowserApp(appName?: string | null): boolean {
  const n = (appName || "").toLowerCase();
  return BROWSERS.some((b) => n.includes(b));
}

/** Best-effort hostname from a URL string. */
function getHost(url?: string | null): string {
  if (!url) return "";
  try {
    const u = url.startsWith("http") ? url : `https://${url}`;
    return new URL(u).hostname.toLowerCase();
  } catch {
    // Not a valid URL — fall back to the raw string so keyword tests still run.
    return url.toLowerCase();
  }
}

/** First bucket whose keyword list matches `hay`, else null. */
function matchGroups(
  hay: string,
  groups: Record<Category, string[]>,
): Category | null {
  const order: Category[] = ["productive", "neutral", "unproductive"];
  for (const cat of order) {
    if (groups[cat].some((kw) => hay.includes(kw))) return cat;
  }
  return null;
}

function classifyFile(path: string): Category {
  const ext = path.split(".").pop()?.toLowerCase() || "";
  const cat = (["productive", "neutral", "unproductive"] as Category[]).find(
    (c) => FILE_EXT[c].includes(ext),
  );
  return cat || "neutral";
}

/**
 * Classify a single activity item from any of its signals.
 * Priority: the website (if any) → the file opened → the app → default neutral.
 */
export function classifyActivity(a: {
  app_name?: string | null;
  url?: string | null;
  title?: string | null;
  file_path?: string | null;
}): Category {
  // 1) Web signal wins — a browser's usefulness is the site it's showing.
  const host = getHost(a.url);
  const webHay = `${host} ${(a.title || "").toLowerCase()} ${(a.url || "").toLowerCase()}`;
  const web = matchGroups(webHay, WEB);
  if (web) return web;

  // 2) A file that was opened — judge by its type.
  if (a.file_path) return classifyFile(a.file_path);

  // 3) The application itself.
  const app = (a.app_name || "").toLowerCase();
  const appCat = matchGroups(app, APP);
  if (appCat) return appCat;

  // 4) A browser with no identifiable site → neutral (unknown browsing).
  if (isBrowserApp(app)) return "neutral";

  // 5) Unknown app / no signal.
  return "neutral";
}

export interface ProductivitySummary {
  productive: number; // minutes
  neutral: number;
  unproductive: number;
  classified: number; // productive + neutral + unproductive
  /** productive ÷ (productive + unproductive), 0–100. Neutral is excluded so
   *  OS/system/email time doesn't dilute the score. */
  score: number;
}

/**
 * Roll a day's activity into productive/neutral/unproductive minutes.
 * Browser apps are counted via their sites (not the browser shell) to avoid
 * double-counting; any browser time not attributed to a site falls to neutral.
 */
export function summarizeSession(input: {
  appUsage: Array<{ app_name: string; duration_mins: number }>;
  sites: Array<{ url?: string; title?: string; duration_mins: number }>;
}): ProductivitySummary {
  const mins: Record<Category, number> = {
    productive: 0,
    neutral: 0,
    unproductive: 0,
  };

  let browserAppMins = 0;
  for (const app of input.appUsage || []) {
    const dur = app.duration_mins || 0;
    if (isBrowserApp(app.app_name)) {
      browserAppMins += dur; // handled via sites below
      continue;
    }
    mins[classifyActivity({ app_name: app.app_name })] += dur;
  }

  let siteMins = 0;
  for (const s of input.sites || []) {
    const dur = s.duration_mins || 0;
    siteMins += dur;
    mins[classifyActivity({ url: s.url, title: s.title })] += dur;
  }

  // Browser time not attributed to any site → count as neutral, so the totals
  // still add up to roughly the tracked browser time.
  if (browserAppMins > siteMins) mins.neutral += browserAppMins - siteMins;

  const classified = mins.productive + mins.neutral + mins.unproductive;
  const denom = mins.productive + mins.unproductive;
  const score = denom > 0 ? Math.round((mins.productive / denom) * 100) : 0;

  return { ...mins, classified, score };
}
